import { useEffect, useSyncExternalStore } from 'react';

import { useGlobalContext } from '../context/global';
import { type ChatThreadRef, localThreadId } from '../lib/chat-thread-ref';
import { getThreadState, subscribeThreadStates } from '../lib/chat-thread-store';
import { type IChatThreadState } from './useChatThread';

/**
 * The conversation with one parent or child, for a panel to render.
 *
 * <p>The ENGINE is not here. It is mounted by `PeerThreadHosts` in the layout, where it outlives
 * this panel, and publishes into the thread store; this hook asks the layout to host the thread
 * and reads whatever the engine last published. Null until the host has rendered once.</p>
 */
export const usePeerChat = (counterpartCoreUserId: number, electionId: number): IChatThreadState | null => {
	const { peerThreads } = useGlobalContext();
	const ref: ChatThreadRef | null =
		counterpartCoreUserId > 0 && electionId > 0 ? { kind: 'peer', counterpartCoreUserId, electionId } : null;
	const localId = ref ? localThreadId(ref) : '';

	const openThread = peerThreads?.openThread;
	useEffect(() => {
		if (!ref || !openThread) return;
		openThread(ref);
		// The ref is rebuilt each render; its two numbers are the identity.
	}, [counterpartCoreUserId, electionId, openThread]);

	const state = useSyncExternalStore(
		subscribeThreadStates,
		() => (localId ? getThreadState<IChatThreadState>(localId) : undefined),
		() => undefined
	);

	return state ?? null;
};
